/**
 * Reads the signed-in user's roles from `public.user_roles`.
 *
 * The backend's auth middleware is authoritative — every write still
 * validates against the same table server-side. This hook exists only to
 * drive UI affordances: hiding nav items the user can't reach, showing
 * the right landing page, surfacing a friendly 403 before round-tripping.
 *
 * ## Why the client reads user_roles directly
 *
 * - RLS on the table restricts `select` to `auth.uid() = user_id`, so a
 *   malicious client can't enumerate other users' roles.
 * - The JWT itself carries no roles claim today; we considered stuffing
 *   them in via a Postgres function, but the extra complexity isn't
 *   worth the marginal latency saving — this is one small query on
 *   sign-in and it's cached for the session.
 *
 * ## Role hierarchy
 *
 * `admin ⊇ manager ⊇ agent` on the staff side; `customer` stands alone.
 * The hook returns the raw roles array and exposes `has(role)` that
 * respects the hierarchy — callers should use `has()` rather than
 * `roles.includes()` so a staff user auto-satisfies agent-level checks.
 */
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { useSession } from "@/hooks/auth/useSession";
import { supabase } from "@/lib/supabaseClient";

export const AppRoleSchema = z.enum(["customer", "agent", "manager", "admin"]);
export type AppRole = z.infer<typeof AppRoleSchema>;

const UserRolesRowSchema = z.object({ role: AppRoleSchema });

const STAFF_HIERARCHY: Record<AppRole, AppRole[]> = {
  admin: ["admin", "manager", "agent"],
  manager: ["manager", "agent"],
  agent: ["agent"],
  customer: ["customer"],
};

async function fetchRoles(userId: string): Promise<AppRole[]> {
  const { data, error } = await supabase.from("user_roles").select("role").eq("user_id", userId);
  if (error) throw error;
  return z.array(UserRolesRowSchema).parse(data ?? []).map((r) => r.role);
}

export function useRoles() {
  const { data: session, isLoading: sessionLoading } = useSession();
  const userId = session?.user.id ?? null;

  const query = useQuery<AppRole[]>({
    queryKey: ["auth", "roles", userId],
    queryFn: () => fetchRoles(userId as string),
    enabled: !!userId,
    staleTime: Infinity, // replaced by the useSession invalidate on auth events
  });

  const roles = query.data ?? [];

  /**
   * Returns true if the user effectively has the given role, expanding
   * the staff hierarchy (admin satisfies manager satisfies agent).
   */
  function has(role: AppRole): boolean {
    return roles.some((held) => STAFF_HIERARCHY[held]?.includes(role) ?? false);
  }

  /** True if the user holds any staff role (agent/manager/admin). */
  function isStaff(): boolean {
    return has("agent");
  }

  return {
    roles,
    has,
    isStaff,
    isLoading: sessionLoading || query.isLoading,
    error: query.error,
  };
}
